import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useApiGet } from '../../utils/httpClientTs';
import { getUserCommittees } from '../../endpoints/usersTs';
import { UserCommitteesResponse, MemberPositionItem } from '../../types/users';
import { getCalendarYearStart } from '../../utils/variables';
import dayjs from 'dayjs';
import MemberActionModal from '../../Components/MemberActionModal';

interface UserMemberInfoProps {
  id: string;
  canAction: boolean;
  refetchParent: () => void;
  needToRefetch: boolean;
  updateNeedToRefetch: (value: boolean) => void;
}

const UserMemberInfo: React.FC<UserMemberInfoProps> = ({
  id,
  canAction,
  refetchParent,
  needToRefetch,
  updateNeedToRefetch,
}) => {
  const [showMoreChapters, setShowMoreChapters] = useState<boolean>(false);
  const [showMoreCommittees, setShowMoreCommittees] = useState<boolean>(false);
  const [showMoreOfficers, setShowMoreOfficers] = useState<boolean>(false);
  const [showMoreTLC, setShowMoreTLC] = useState<boolean>(false);
  const [showMoreBnB, setShowMoreBnB] = useState<boolean>(false);
  const [showMoreReciprocity, setShowMoreReciprocity] =
    useState<boolean>(false);

  const [showActionModal, setShowActionModal] = useState<boolean>(false);
  const [actionType, setActionType] = useState<
    'transfer' | 'reinstatement' | 'initiation'
  >('reinstatement');

  const {
    data: userInfo,
    isLoading,
    refetch,
  } = useApiGet<UserCommitteesResponse>(
    ['userCommittees', id],
    () => getUserCommittees({ id }),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  const handleMakeRefetch = () => {
    if (needToRefetch) {
      return;
    } else {
      updateNeedToRefetch(true);
    }
  };

  const transferWords = (): string => {
    let transfer = false;
    if (userInfo?.chapter_history?.length > 0) {
      userInfo.chapter_history.forEach((item) => {
        if (item.end_date === null) {
          transfer = true;
        } else if (
          dayjs(item.end_date).isAfter(dayjs(getCalendarYearStart()))
        ) {
          transfer = true;
        }
      });
    }
    return transfer ? 'Transfer' : 'Reinstate';
  };

  const handleInitiate = () => {
    setActionType('initiation');
    setShowActionModal(true);
  };

  const handleReinstate = () => {
    const type = transferWords() === 'Transfer' ? 'transfer' : 'reinstatement';
    setActionType(type);
    setShowActionModal(true);
  };

  const getPositionName = (item: MemberPositionItem): string => {
    let positionName = item.position;
    if (item.local_chapter_name) {
      positionName = `${item.position} (${item.local_chapter_name})`;
    }
    if (item.spd_chapter_name) {
      positionName = `${item.position} (${item.spd_chapter_name})`;
    }
    return positionName;
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={12} sx={{ p: 2 }}>
          <Typography variant="h6">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }
  if (!userInfo) {
    return (
      <Grid container>
        <Grid item xs={12} sx={{ p: 2 }}>
          <Typography variant="h6">User not found</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      {userInfo?.chapter_history?.length === 0 && canAction && (
        <Grid item xs={12} sx={{ p: 2 }} textAlign={'right'}>
          <Button
            variant="contained"
            size="small"
            onClick={handleInitiate}
            sx={{ fontWeight: 'bold' }}
          >
            {'Initiate'}
          </Button>
        </Grid>
      )}
      {userInfo?.chapter_history && userInfo.chapter_history.length > 0 && (
        <Grid item xs={12} md={6} sx={{ p: 2 }}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <Grid container justifyContent={'space-between'}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" component={'span'}>
                      Chapters
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowMoreChapters(!showMoreChapters)}
                      sx={{ ml: 2 }}
                    >
                      {showMoreChapters ? 'Show Less' : 'Show More'}
                    </Button>
                  </Grid>
                  {canAction && (
                    <Grid item xs={12} sm={6} textAlign={'right'}>
                      <LoadingButton
                        variant="contained"
                        size="small"
                        onClick={handleReinstate}
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                      >
                        {transferWords()}
                      </LoadingButton>
                    </Grid>
                  )}
                </Grid>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreChapters && (
                <Grid container>
                  {userInfo.chapter_history.map((item) => (
                    <Grid item container xs={12} key={item.id} sx={{ p: 2 }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                        {item.initiated && (
                          <Typography
                            variant="caption"
                            component={'span'}
                            color={'primary'}
                            sx={{ ml: 2, fontWeight: 'bold' }}
                          >
                            Initiated
                          </Typography>
                        )}
                        {item.reinstated && (
                          <Typography
                            variant="caption"
                            component={'span'}
                            color={'primary'}
                            sx={{ ml: 2, fontWeight: 'bold' }}
                          >
                            Reinstated
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {dayjs(item.start_date).format('MM/DD/YYYY')} -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {userInfo?.officers && userInfo.officers.length > 0 && (
        <Grid item xs={12} md={6} sx={{ p: 2 }}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    Chapter Leadership
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreOfficers(!showMoreOfficers)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreOfficers ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreOfficers && (
                <Grid container>
                  {userInfo.officers.map((item) => (
                    <Grid item container xs={12} key={item.id} sx={{ p: 2 }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {userInfo?.committees && userInfo.committees.length > 0 && (
        <Grid item xs={12} md={6} sx={{ p: 2 }}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    Committees
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreCommittees(!showMoreCommittees)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreCommittees ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreCommittees && (
                <Grid container>
                  {userInfo.committees.map((item) => (
                    <Grid item container xs={12} key={item.id} sx={{ p: 2 }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {userInfo?.tlc && userInfo.tlc.length > 0 && (
        <Grid item xs={12} md={6} sx={{ p: 2 }}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    TLCs
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreTLC(!showMoreTLC)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreTLC ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreTLC && (
                <Grid container>
                  {userInfo.tlc.map((item) => (
                    <Grid item container xs={12} key={item.id} sx={{ p: 2 }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
      {userInfo?.bnb && userInfo.bnb.length > 0 && (
        <Grid item xs={12} md={6} sx={{ p: 2 }}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    Bed and Breakfast
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreBnB(!showMoreBnB)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreBnB ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreBnB && (
                <Grid container>
                  {userInfo.bnb.map((item) => (
                    <Grid item container xs={12} key={item.id} sx={{ p: 2 }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {userInfo?.reciprocity && userInfo.reciprocity.length > 0 && (
        <Grid item xs={12} md={6} sx={{ p: 2 }}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    Reciprocity
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreReciprocity(!showMoreReciprocity)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreReciprocity ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreReciprocity && (
                <Grid container>
                  {userInfo.reciprocity.map((item) => (
                    <Grid item container xs={12} key={item.id} sx={{ p: 2 }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
      {(!userInfo.tlc || userInfo.tlc.length === 0) &&
        (!userInfo.bnb || userInfo.bnb.length === 0) &&
        (!userInfo.reciprocity || userInfo.reciprocity.length === 0) &&
        (!userInfo.committees || userInfo.committees.length === 0) &&
        (!userInfo.officers || userInfo.officers.length === 0) &&
        (!userInfo.chapter_history ||
          userInfo.chapter_history.length === 0) && (
          <Grid item xs={12} sx={{ p: 2 }} textAlign="center">
            <Typography variant="body1">No membership information</Typography>
          </Grid>
        )}
      <MemberActionModal
        open={showActionModal}
        onClose={() => setShowActionModal(false)}
        userId={id}
        actionType={actionType}
        onSuccess={() => {
          refetch();
          refetchParent();
          handleMakeRefetch();
        }}
      />
    </Grid>
  );
};

export default UserMemberInfo;
